import { createContext, useContext } from "react";
import type { ItemAtVersion } from "../../threads/prepare";

/**
 * What a node's controls do, given by the view that draws the graph: React Flow passes
 * nodes only their data, so the callbacks travel by context.
 */
export interface GraphActions {
  /** Open or close an expandable node in place: its own steps drawn inside it. */
  readonly toggle: (nodeId: string) => void;
  /** Open or close a card's settings, on the card. */
  readonly toggleCard: (nodeId: string) => void;
  /**
   * In a Thread: the address of the latest entry of a step (its flattened key), or `null`
   * while it has none. The function is `null` outside a Thread.
   */
  readonly stepLinkInThread: ((progressKey: string) => string | null) | null;
  /**
   * A card's Edit: prepares the edit Thread on its item, beside the graph (F51, WA-11); `null`
   * without a project to edit in.
   */
  readonly edit: ((item: ItemAtVersion) => void) | null;
}

/**
 * The read view provides these; the editor needs none, because its cards offer no chevron
 * or settings (`cardInteraction`). A component that asks for them without a provider is a
 * mistake, and says so.
 */
export const GraphActionsContext = createContext<GraphActions | null>(null);

export function useGraphActions(): GraphActions {
  const actions = useContext(GraphActionsContext);
  if (actions === null) {
    throw new Error("useGraphActions needs a GraphActionsContext provider");
  }
  return actions;
}
